import React, { useState, useEffect, useRef } from 'react';
import { Plus, Check, X, ChevronUp, ChevronDown, Settings, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import api from '../../services/api';
import { useAuthContext } from '../../contexts/AuthContext';

interface LeaveRequest {
  id: number;
  employee_email: string;
  employee_name: string;
  leave_type: string;
  from_date: string;
  to_date: string;
  days: number;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewed_by: string | null;
  review_note: string | null;
  created_at: string;
}

interface Balance {
  total: number;
  used: number;
  remaining: number;
}

interface EmployeeBalance {
  employee_email: string;
  employee_name: string;
  balance: Record<string, Balance>;
}

interface UserRow {
  user_email: string;
  user_name: string;
}

const LEAVE_TYPES = ['ANNUAL', 'SICK', 'CASUAL', 'UNPAID'];

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-50 text-yellow-700',
  APPROVED: 'bg-green-50 text-green-700',
  REJECTED: 'bg-red-50 text-red-700',
};

const QUOTA_COLORS: Record<string, string> = {
  ANNUAL: 'bg-blue-50 border-blue-200',
  SICK: 'bg-red-50 border-red-200',
  CASUAL: 'bg-purple-50 border-purple-200',
  UNPAID: 'bg-gray-50 border-gray-200',
};

const LeaveManagementPage: React.FC = () => {
  const { userRole, user, isAdmin, isTeamLead } = useAuthContext();
  const canManage = isAdmin;

  const [tab, setTab] = useState<'my' | 'admin'>(isAdmin ? 'admin' : 'my');
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [balance, setBalance] = useState<Record<string, Balance>>({});
  const [allBalances, setAllBalances] = useState<EmployeeBalance[]>([]);
  const [adminLeaves, setAdminLeaves] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [reviewModal, setReviewModal] = useState<{ leave: LeaveRequest; action: 'APPROVED' | 'REJECTED' } | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [quotaModal, setQuotaModal] = useState<{ email: string; name: string } | null>(null);
  const [quotaForm, setQuotaForm] = useState({ leave_type: 'ANNUAL', total_days: '' });
  const [filterStatus, setFilterStatus] = useState('');
  const [adminFilterStatus, setAdminFilterStatus] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState({
    leave_type: 'ANNUAL',
    from_date: '',
    to_date: '',
    reason: '',
  });

  const loadMyData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      params.append('me', 'true');
      const [leavesRes, balanceRes] = await Promise.all([
        api.get(`/leave/?${params.toString()}`),
        api.get(`/leave/my-balance?year=${currentYear}`),
      ]);
      if (leavesRes.data.success) setLeaves(
        leavesRes.data.data.filter((l: LeaveRequest) => l.employee_email === user?.email)
      );
      if (balanceRes.data.success) setBalance(balanceRes.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (adminFilterStatus) params.append('status', adminFilterStatus);
      const [leavesRes, balancesRes] = await Promise.all([
        api.get(`/leave/?${params.toString()}`),
        api.get(`/leave/admin/all-balances?year=${currentYear}`),
      ]);
      if (leavesRes.data.success) setAdminLeaves(leavesRes.data.data);
      if (balancesRes.data.success) setAllBalances(balancesRes.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'my') loadMyData();
    else loadAdminData();
  }, [tab, filterStatus, adminFilterStatus]);

  const handleSubmit = async () => {
    if (!form.from_date || !form.to_date) return;
    try {
      await api.post('/leave/', form);
      setShowModal(false);
      setForm({ leave_type: 'ANNUAL', from_date: '', to_date: '', reason: '' });
      loadMyData();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to submit leave');
    }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    try {
      await api.put(`/leave/${reviewModal.leave.id}/review`, {
        status: reviewModal.action,
        review_note: reviewNote,
      });
      setReviewModal(null);
      setReviewNote('');
      loadAdminData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await api.delete(`/leave/${id}`);
      tab === 'my' ? loadMyData() : loadAdminData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpsertQuota = async () => {
    if (!quotaModal || !quotaForm.total_days) return;
    try {
      await api.post('/leave/admin/quotas', {
        employee_email: quotaModal.email,
        leave_type: quotaForm.leave_type,
        total_days: parseInt(quotaForm.total_days),
        year: currentYear,
      });
      setQuotaModal(null);
      setQuotaForm({ leave_type: 'ANNUAL', total_days: '' });
      loadAdminData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(prev =>
      prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const sortedLeaves = (data: LeaveRequest[]) => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const av = (a as any)[sortConfig.key] ?? '';
      const bv = (b as any)[sortConfig.key] ?? '';
      return sortConfig.dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  };

  const pendingCount = adminLeaves.filter(l => l.status === 'PENDING').length;
  const pendingLeaves = adminLeaves.filter(l => l.status === 'PENDING');
  const otherLeaves = adminLeaves.filter(l => l.status !== 'PENDING');

  const [calMonth, setCalMonth] = useState(new Date(currentYear, new Date().getMonth(), 1));
  const [reportEmail, setReportEmail] = useState('');
  const [reportFrom, setReportFrom] = useState(`${currentYear}-01-01`);
  const [reportTo, setReportTo] = useState(`${currentYear}-12-31`);
  const printRef = useRef<HTMLDivElement>(null);

  const reportEmployee = allBalances.find(e => e.employee_email === reportEmail);
  const reportLeaves = adminLeaves.filter(l => {
    if (reportEmail && l.employee_email !== reportEmail) return false;
    if (reportFrom && l.from_date < reportFrom) return false;
    if (reportTo && l.to_date > reportTo) return false;
    return l.status === 'APPROVED' || l.status === 'REJECTED' || l.status === 'PENDING';
  });
  const reportTotals = LEAVE_TYPES.reduce((acc, lt) => {
    acc[lt] = reportLeaves.filter(l => l.leave_type === lt && l.status === 'APPROVED').reduce((s, l) => s + l.days, 0);
    return acc;
  }, {} as Record<string, number>);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<html><head><title>Leave Report</title><style>
      body { font-family: sans-serif; padding: 24px; color: #111; }
      h1 { font-size: 18px; margin-bottom: 4px; }
      p { font-size: 13px; color: #555; margin: 2px 0 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th { background: #f3f4f6; text-align: left; padding: 8px 10px; border: 1px solid #e5e7eb; font-size: 11px; text-transform: uppercase; }
      td { padding: 8px 10px; border: 1px solid #e5e7eb; }
      .summary { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
      .summary-card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 16px; min-width: 100px; }
      .summary-card .label { font-size: 11px; color: #6b7280; text-transform: uppercase; }
      .summary-card .value { font-size: 20px; font-weight: bold; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; }
      .APPROVED { background: #dcfce7; color: #166534; }
      .PENDING { background: #fef9c3; color: #854d0e; }
      .REJECTED { background: #fee2e2; color: #991b1b; }
      @media print { body { padding: 0; } }
    </style></head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  const LeaveCalendar = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const calLeaves = adminLeaves.filter(l => l.status === 'PENDING' || l.status === 'APPROVED');

    const getLeavesForDay = (day: number) => {
      const date = new Date(year, month, day);
      return calLeaves.filter(l => {
        const from = new Date(l.from_date);
        const to = new Date(l.to_date);
        from.setHours(0,0,0,0); to.setHours(0,0,0,0); date.setHours(0,0,0,0);
        return date >= from && date <= to;
      });
    };

    const cells = Array.from({ length: firstDay }, (_, i) => <td key={`e${i}`} className="p-1 border border-gray-100" />);
    for (let d = 1; d <= daysInMonth; d++) {
      const dayLeaves = getLeavesForDay(d);
      const isToday = new Date().getDate() === d && new Date().getMonth() === month && new Date().getFullYear() === year;
      cells.push(
        <td key={d} className={`p-1 border border-gray-100 align-top min-w-[80px] h-20 ${isToday ? 'bg-blue-50' : 'bg-white'}`}>
          <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>{d}</div>
          <div className="space-y-0.5 overflow-hidden">
            {dayLeaves.slice(0, 3).map(l => (
              <div
                key={l.id}
                title={`${l.employee_name} — ${l.leave_type}`}
                className={`text-xs px-1 rounded truncate ${l.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}
              >
                {l.employee_name.split(' ')[0]}
              </div>
            ))}
            {dayLeaves.length > 3 && <div className="text-xs text-gray-400">+{dayLeaves.length - 3}</div>}
          </div>
        </td>
      );
    }

    const rows: React.ReactNode[] = [];
    let row: React.ReactNode[] = [];
    cells.forEach((cell, i) => {
      row.push(cell);
      if ((i + 1) % 7 === 0) { rows.push(<tr key={i}>{row}</tr>); row = []; }
    });
    if (row.length) {
      while (row.length < 7) row.push(<td key={`f${row.length}`} className="p-1 border border-gray-100" />);
      rows.push(<tr key="last">{row}</tr>);
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-base font-semibold text-gray-900">Leave Calendar</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs">
              <span className="inline-block w-3 h-3 rounded bg-yellow-100 border border-yellow-300" /> Pending
              <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300 ml-2" /> Approved
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCalMonth(new Date(year, month - 1, 1))} className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium w-32 text-center">{calMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
              <button onClick={() => setCalMonth(new Date(year, month + 1, 1))} className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <th key={d} className="p-2 text-xs font-medium text-gray-500 text-center border border-gray-100 bg-gray-50">{d}</th>
              ))}</tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      </div>
    );
  };

  const SortIcon = ({ col }: { col: string }) =>
    sortConfig?.key === col
      ? sortConfig.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      : <ChevronUp className="w-3 h-3 opacity-30" />;

  const LeaveTable = ({ data, showEmployee }: { data: LeaveRequest[]; showEmployee: boolean }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {showEmployee && (
              <th onClick={() => handleSort('employee_name')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                <div className="flex items-center gap-1">Employee <SortIcon col="employee_name" /></div>
              </th>
            )}
            <th onClick={() => handleSort('leave_type')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center gap-1">Type <SortIcon col="leave_type" /></div>
            </th>
            <th onClick={() => handleSort('from_date')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center gap-1">From <SortIcon col="from_date" /></div>
            </th>
            <th onClick={() => handleSort('to_date')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center gap-1">To <SortIcon col="to_date" /></div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
            <th onClick={() => handleSort('status')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center gap-1">Status <SortIcon col="status" /></div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {loading ? (
            <tr><td colSpan={showEmployee ? 8 : 7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={showEmployee ? 8 : 7} className="px-4 py-8 text-center text-gray-500">No leave requests found</td></tr>
          ) : sortedLeaves(data).map(leave => (
            <tr key={leave.id} className="hover:bg-gray-50">
              {showEmployee && (
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="font-medium">{leave.employee_name}</div>
                  <div className="text-xs text-gray-400">{leave.employee_email}</div>
                </td>
              )}
              <td className="px-4 py-3">
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">{leave.leave_type}</span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">{new Date(leave.from_date).toLocaleDateString('en-GB')}</td>
              <td className="px-4 py-3 text-sm text-gray-900">{new Date(leave.to_date).toLocaleDateString('en-GB')}</td>
              <td className="px-4 py-3 text-sm text-gray-900">{leave.days}</td>
              <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{leave.reason || '-'}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[leave.status]}`}>{leave.status}</span>
                {leave.review_note && (
                  <div className="text-xs text-gray-400 mt-1 max-w-xs truncate" title={leave.review_note}>{leave.review_note}</div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {canManage && leave.status === 'PENDING' && (
                    <>
                      <button onClick={() => setReviewModal({ leave, action: 'APPROVED' })} className="text-green-600 hover:text-green-800" title="Approve"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setReviewModal({ leave, action: 'REJECTED' })} className="text-red-500 hover:text-red-700" title="Reject"><X className="w-4 h-4" /></button>
                    </>
                  )}
                  {leave.status === 'PENDING' && leave.employee_email === user?.email && (
                    <button onClick={() => handleDelete(leave.id)} className="text-gray-400 hover:text-red-600" title="Cancel"><X className="w-4 h-4" /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
            <p className="text-sm text-gray-500">{currentYear} — {user?.name || user?.email}</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Apply Leave
          </button>
        </div>

        {/* Tabs */}
        {canManage && (
          <div className="mb-6 flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setTab('my')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'my' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              My Leaves
            </button>
            <button
              onClick={() => setTab('admin')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'admin' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Admin Panel
            </button>
          </div>
        )}

        {/* MY LEAVES TAB */}
        {tab === 'my' && (
          <>
            {/* Balance Cards */}
            <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {LEAVE_TYPES.map(lt => {
                const b = balance[lt] || { total: 0, used: 0, remaining: 0 };
                return (
                  <div key={lt} className={`p-4 rounded-lg border ${QUOTA_COLORS[lt]}`}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{lt}</p>
                    <p className="text-2xl font-bold text-gray-900">{b.remaining} <span className="text-sm font-normal text-gray-500">left</span></p>
                    <p className="text-xs text-gray-500 mt-1">{b.used} used / {b.total} total</p>
                  </div>
                );
              })}
            </div>

            {/* Filter */}
            <div className="mb-4 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <LeaveTable data={leaves} showEmployee={false} />
          </>
        )}

        {/* ADMIN TAB */}
        {tab === 'admin' && canManage && (
          <>
            {/* Leave Calendar */}
            <LeaveCalendar />

            {/* All Leave Requests */}
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">All Leave Requests</h2>
                <select value={adminFilterStatus} onChange={e => setAdminFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
              <LeaveTable data={adminLeaves} showEmployee={true} />
            </div>

            {/* Leave Report */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-base font-semibold text-gray-900">Leave Report</h2>
                <button
                  onClick={handlePrint}
                  disabled={reportLeaves.length === 0}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
              </div>
              <div className="p-4 flex flex-wrap gap-4 items-end border-b">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                  <select value={reportEmail} onChange={e => setReportEmail(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[200px]">
                    <option value="">All Employees</option>
                    {allBalances.map(e => <option key={e.employee_email} value={e.employee_email}>{e.employee_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                  <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                  <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              {/* Printable content */}
              <div ref={printRef} className="p-4">
                <h1 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
                  Leave Report — {reportEmployee ? reportEmployee.employee_name : 'All Employees'}
                </h1>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  Period: {new Date(reportFrom).toLocaleDateString('en-GB')} – {new Date(reportTo).toLocaleDateString('en-GB')}
                </p>

                {/* Summary totals */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {LEAVE_TYPES.map(lt => (
                    <div key={lt} className={`px-4 py-2 rounded-lg border ${QUOTA_COLORS[lt]}`}>
                      <div className="text-xs font-medium text-gray-500 uppercase">{lt}</div>
                      <div className="text-xl font-bold text-gray-900">{reportTotals[lt]} <span className="text-xs font-normal text-gray-500">days approved</span></div>
                    </div>
                  ))}
                </div>

                {/* Detail table */}
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {!reportEmail && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">Employee</th>}
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">From</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">To</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">Days</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">Reason</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportLeaves.length === 0 ? (
                      <tr><td colSpan={reportEmail ? 6 : 7} className="px-4 py-6 text-center text-gray-400">No records found</td></tr>
                    ) : reportLeaves.map(l => (
                      <tr key={l.id} className="hover:bg-gray-50">
                        {!reportEmail && (
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900 text-sm">{l.employee_name}</div>
                            <div className="text-xs text-gray-400">{l.employee_email}</div>
                          </td>
                        )}
                        <td className="px-4 py-2"><span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">{l.leave_type}</span></td>
                        <td className="px-4 py-2 text-sm text-gray-900">{new Date(l.from_date).toLocaleDateString('en-GB')}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{new Date(l.to_date).toLocaleDateString('en-GB')}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{l.days}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate">{l.reason || '-'}</td>
                        <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[l.status]}`}>{l.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* All Employees Balance */}
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Employee Leave Balances — {currentYear}</h2>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      {LEAVE_TYPES.map(lt => (
                        <th key={lt} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{lt}</th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allBalances.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No employees found</td></tr>
                    ) : allBalances.map(emp => (
                      <tr key={emp.employee_email} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{emp.employee_name}</div>
                          <div className="text-xs text-gray-400">{emp.employee_email}</div>
                        </td>
                        {LEAVE_TYPES.map(lt => {
                          const b = emp.balance[lt] || { total: 0, used: 0, remaining: 0 };
                          return (
                            <td key={lt} className="px-4 py-3 text-center text-sm">
                              <span className={`font-semibold ${b.remaining < 0 ? 'text-red-600' : 'text-gray-900'}`}>{b.remaining}</span>
                              <span className="text-gray-400 text-xs"> / {b.total}</span>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setQuotaModal({ email: emp.employee_email, name: emp.employee_name })}
                            className="text-gray-500 hover:text-blue-600"
                            title="Set Quota"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>


          </>
        )}
      </div>

      {/* Apply Leave Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Apply for Leave</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
                <select
                  value={form.leave_type}
                  onChange={e => setForm({ ...form, leave_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {LEAVE_TYPES.map(t => {
                    const b = balance[t] || { remaining: 0, total: 0 };
                    return (
                      <option key={t} value={t}>
                        {t} {t !== 'UNPAID' ? `(${b.remaining} days remaining)` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
                  <input
                    type="date"
                    value={form.from_date}
                    onChange={e => setForm({ ...form, from_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
                  <input
                    type="date"
                    value={form.to_date}
                    onChange={e => setForm({ ...form, to_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              {form.from_date && form.to_date && (
                <p className="text-sm text-blue-600 font-medium">
                  {Math.max(0, (new Date(form.to_date).getTime() - new Date(form.from_date).getTime()) / 86400000 + 1)} day(s)
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  rows={3}
                  placeholder="Optional reason"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={!form.from_date || !form.to_date}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {reviewModal.action === 'APPROVED' ? 'Approve' : 'Reject'} Leave
              </h2>
              <button onClick={() => setReviewModal(null)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{reviewModal.leave.employee_name}</span> — {reviewModal.leave.leave_type} ({reviewModal.leave.days} day{reviewModal.leave.days > 1 ? 's' : ''})
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setReviewModal(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleReview}
                className={`px-4 py-2 text-sm text-white rounded-lg ${reviewModal.action === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {reviewModal.action === 'APPROVED' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Quota Modal */}
      {quotaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Set Leave Quota</h2>
                <p className="text-sm text-gray-500">{quotaModal.name}</p>
              </div>
              <button onClick={() => setQuotaModal(null)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                <select
                  value={quotaForm.leave_type}
                  onChange={e => setQuotaForm({ ...quotaForm, leave_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Days for {currentYear}</label>
                <input
                  type="number"
                  min="0"
                  value={quotaForm.total_days}
                  onChange={e => setQuotaForm({ ...quotaForm, total_days: e.target.value })}
                  placeholder="e.g. 12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setQuotaModal(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleUpsertQuota}
                disabled={!quotaForm.total_days}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagementPage;
